/*
==================================================
CART MOBILE SHELL

Функция:
визуальный каркас mobile cart modal

Родитель:
cart-mobile-modal/index.tsx

Дочерние элементы:
cart-mobile-header
cart-mobile-promo
cart-mobile-points
cart-mobile-footer

Использует:
scroll area
zigzag mask
body slot
footer slot

Примечания:
shell сам управляет zigzag, scroll math
и сбрасывает scroll/zigzag при смене step
==================================================
*/

"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import CartMobileHeader from "./cart-mobile-header";
import CartMobilePromo from "./cart-mobile-promo";
import CartMobilePoints from "./cart-mobile-points";
import CartMobileFooter from "./cart-mobile-footer";

type Step = "cart" | "address" | "shipping" | "review";

type CartMobileShellProps = {
  open: boolean;
  onClose: () => void;
  step: Step;
  modalTitle: string;
  hasCartItems: boolean;
  promoOpen: boolean;
  setPromoOpen: (value: boolean) => void;
  body: React.ReactNode;
  footerProps: any;
  cart: any;
  shippingOptions: any;
};

export default function CartMobileShell({
  open,
  onClose,
  step,
  modalTitle,
  hasCartItems,
  promoOpen,
  setPromoOpen,
  body,
  footerProps,
  cart,
  shippingOptions,
}: CartMobileShellProps) {
  const [tornProgress, setTornProgress] = useState(1);
  const [isDesktop] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const zigzagSize = isDesktop ? "8px" : "40px";
  const maxZigzagHeight = isDesktop ? 25 : 15;
  const maskCompensation = 0.3;

  const currentZigzagHeight = useMemo(() => {
    return `${Math.max(0, Math.round(maxZigzagHeight * tornProgress))}px`;
  }, [maxZigzagHeight, tornProgress]);

  const maskImage = useMemo(() => {
    return `linear-gradient(to bottom, black calc(${100 + maskCompensation}% - ${currentZigzagHeight}), transparent calc(100% - ${currentZigzagHeight})), conic-gradient(from -45deg at bottom, black 25%, transparent 0)`;
  }, [currentZigzagHeight]);

  const handleScroll = () => {
    if (!scrollRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const fadeZone = 100;
    const progress = Math.max(0, Math.min(1, distanceFromBottom / fadeZone));

    setTornProgress(progress);
  };

  useEffect(() => {
    if (!open) return;

    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }

    setTornProgress(1);
  }, [step, open]);

  return (
    <div
      className={`fixed inset-0 z-[250] ${
        open ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"
      } md:transition-opacity md:duration-300 md:ease-out`}
    >
      <div
        className="absolute inset-0 bg-black/55 backdrop-blur-[2px]"
        onClick={onClose}
      />

      <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-3 md:items-start md:justify-end md:pr-80 md:pt-20">
        <div
          className={`
            relative h-[88vh] w-full max-w-[480px] md:h-[700px] md:max-w-[500px]
            flex flex-col overflow-hidden rounded-[1.5rem]
            bg-gradient-to-r
            from-[#0f172af2]
            via-[#132238f2]
            to-[#1e293bf2]
            shadow-[0_20px_50px_rgba(0,0,0,0.5)] pointer-events-auto touch-pan-y
            md:transition-all md:duration-300 md:ease-out
            ${open ? "translate-y-0 scale-100" : "md:translate-y-4 md:scale-95"}
          `}
        >
          <CartMobileHeader
            modalTitle={modalTitle}
            cart={cart}
            shippingOptions={shippingOptions}
            onClose={onClose}
          />

          {hasCartItems && (
            <div className="mt-3 flex items-center gap-2 px-1 pb-3">
              <CartMobilePromo onOpenPromo={() => setPromoOpen(true)} />
              <CartMobilePoints points={0} />
            </div>
          )}

          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="no-scrollbar h-[calc(88vh-220px)] overflow-y-auto overscroll-contain px-2 pb-0 pt-0 md:h-[390px]"
            style={{
              WebkitMaskImage: maskImage,
              maskImage: maskImage,
              WebkitMaskPosition: "0 0, 0 100%",
              maskPosition: "0 0, 0 100%",
              WebkitMaskSize: `100% 100%, ${zigzagSize} ${currentZigzagHeight}`,
              maskSize: `100% 100%, ${zigzagSize} ${currentZigzagHeight}`,
              WebkitMaskRepeat: "no-repeat, repeat-x",
              maskRepeat: "no-repeat, repeat-x",
              WebkitOverflowScrolling: "touch",
              overscrollBehaviorY: "contain",
            }}
          >
            {body}
          </div>

          <CartMobileFooter {...footerProps} />

          {promoOpen && cart && (
            <CartMobilePromo
              cart={cart}
              openAsModal
              onClosePromo={() => setPromoOpen(false)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
